import { useState, forwardRef, useImperativeHandle, useMemo } from 'react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe, Stripe, StripePaymentElementChangeEvent, StripePaymentElementOptions } from '@stripe/stripe-js';
import { Box, Typography, CircularProgress, Chip } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { getConfiguration } from '../../shared/configuration/config-settings';
import { stripeInputStyles, stripeLabelStyles } from '../hub-utils';

const config = getConfiguration();
let stripePromise: Promise<Stripe | null> | null = null;
const getStripePromise = () => {
  if (!stripePromise && config.stripe_key) {
    stripePromise = loadStripe(config.stripe_key);
  }
  return stripePromise;
};

interface BillingDetails {
  firstName: string;
  lastName: string;
  email: string;
}

interface PaymentHubCardFormProps {
  isActive: boolean;
  clientSecret: string | null;
  isLoading?: boolean;
  billingDetails: BillingDetails;
  onBillingDetailsChange: (field: keyof BillingDetails, value: string) => void;
  showNameFields?: boolean;
  originalEmail?: string;
}

/**
 * Result from confirming a card payment
 */
export interface CardPaymentResult {
  success: boolean;
  message: string;
  paymentIntentId?: string;
  paymentMethodId?: string;
  status?: string;
  last4?: string;
  brand?: string;
  fingerprint?: string;
}

/**
 * Result from creating a card payment method
 */
export interface CreateCardPaymentMethodResult {
  success: boolean;
  message: string;
  paymentMethodId?: string;
  last4?: string;
  brand?: string;
  fingerprint?: string;
  /** Expiration month */
  expMonth?: number;
  /** Expiration year */
  expYear?: number;
}

export interface CardPaymentRef {
  createPaymentMethod: () => Promise<CreateCardPaymentMethodResult>;
  confirmPayment: (paymentMethodId: string) => Promise<CardPaymentResult>;
  getBillingDetails: () => BillingDetails;
  isReady: boolean;
}

/**
 * Inner form component that uses Stripe hooks (must be inside Elements)
 */
interface InnerFormProps {
  billingDetails: BillingDetails;
  onBillingDetailsChange: (field: keyof BillingDetails, value: string) => void;
  showNameFields?: boolean;
  originalEmail?: string;
  onReadyChange: (ready: boolean) => void;
}

const CardPaymentInnerForm = forwardRef<CardPaymentRef, InnerFormProps>(
  ({ billingDetails, onBillingDetailsChange, showNameFields = false, originalEmail, onReadyChange }, ref) => {
    const stripe = useStripe();
    const elements = useElements();
    const [isReady, setIsReady] = useState(false);
    const [emailBlurred, setEmailBlurred] = useState(false);

    const handlePaymentElementChange = (event: StripePaymentElementChangeEvent) => {
      setIsReady(event.complete);
      onReadyChange(event.complete);
    };

    useImperativeHandle(ref, () => ({
      /**
       * Step 1: Create a PaymentMethod from the card details
       * This returns full card details (last4, brand, fingerprint)
       */
      createPaymentMethod: async (): Promise<CreateCardPaymentMethodResult> => {
        if (!stripe || !elements) {
          return {
            success: false,
            message: 'Stripe has not been initialized.',
          };
        }

        try {
          // Submit the Payment Element to get the payment method
          const { error: submitError } = await elements.submit();
          if (submitError) {
            console.error('[Card] Submit error:', submitError);
            return {
              success: false,
              message: submitError.message || 'Failed to submit card details.',
            };
          }

          // Create the PaymentMethod
          const { error, paymentMethod } = await stripe.createPaymentMethod({
            elements,
            params: {
              billing_details: {
                name: `${billingDetails.firstName} ${billingDetails.lastName}`.trim(),
                email: billingDetails.email,
              },
            },
          });

          if (error) {
            console.error('[Card] CreatePaymentMethod error:', error);
            return {
              success: false,
              message: error.message || 'Failed to create payment method.',
            };
          }

          if (!paymentMethod) {
            return {
              success: false,
              message: 'No payment method returned.',
            };
          }

          console.log('[Card] ✅ PaymentMethod created:', paymentMethod.id);
          console.log('[Card] Card details:', paymentMethod.card);

          return {
            success: true,
            message: 'Payment method created.',
            paymentMethodId: paymentMethod.id,
            last4: paymentMethod.card?.last4,
            brand: paymentMethod.card?.brand,
            fingerprint: paymentMethod.card?.fingerprint ?? undefined,
            expMonth: paymentMethod.card?.exp_month,
            expYear: paymentMethod.card?.exp_year,
          };
        } catch (err) {
          console.error('[Card] Error creating payment method:', err);
          return {
            success: false,
            message: 'An unexpected error occurred.',
          };
        }
      },

      /**
       * Step 2: Confirm the payment using an existing PaymentMethod
       * Called after saving the payment method to our database
       */
      confirmPayment: async (paymentMethodId: string): Promise<CardPaymentResult> => {
        if (!stripe) {
          return {
            success: false,
            message: 'Stripe has not been initialized.',
          };
        }

        // Note: Payment confirmation is handled by the view component
        // which calls the backend to confirm the PaymentIntent
        // This method is here for interface compatibility
        return {
          success: true,
          message: 'Payment method ready for confirmation.',
          paymentMethodId,
        };
      },

      getBillingDetails: () => billingDetails,
      isReady,
    }));

    const paymentElementOptions: StripePaymentElementOptions = {
      layout: 'tabs',
      paymentMethodOrder: ['card'], // Only show card, hide bank account tab
      fields: {
        billingDetails: {
          name: 'never',
          email: 'never',
        },
      },
    };

    return (
      <Box>
        {/* Billing Details */}
        <Box sx={{ mb: 2 }}>
          {/* Name Fields (if needed) */}
          {showNameFields && (
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography component="label" sx={stripeLabelStyles}>
                  First name
                </Typography>
                <input
                  type="text"
                  value={billingDetails.firstName}
                  onChange={(e) => onBillingDetailsChange('firstName', e.target.value)}
                  placeholder="First name"
                  style={stripeInputStyles}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography component="label" sx={stripeLabelStyles}>
                  Last name
                </Typography>
                <input
                  type="text"
                  value={billingDetails.lastName}
                  onChange={(e) => onBillingDetailsChange('lastName', e.target.value)}
                  placeholder="Last name"
                  style={stripeInputStyles}
                />
              </Box>
            </Box>
          )}

          {/* Email Field */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography component="label" sx={stripeLabelStyles}>
                Receipt email
              </Typography>
              {originalEmail && billingDetails.email !== originalEmail && (
                <Chip
                  label="Edited"
                  size="small"
                  color="warning"
                  sx={{ height: 18, fontSize: '10px' }}
                />
              )}
            </Box>
            <input
              type="email"
              value={billingDetails.email}
              onChange={(e) => onBillingDetailsChange('email', e.target.value)}
              onBlur={() => setEmailBlurred(true)}
              onFocus={() => setEmailBlurred(false)}
              placeholder="email@example.com"
              style={stripeInputStyles}
            />
            {/* Info message for new payors */}
            {showNameFields && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1, color: '#0369a1' }}>
                <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                <Typography variant="caption" sx={{ color: '#0369a1' }}>
                  A new champion will be created and a user invitation will be sent to this email
                </Typography>
              </Box>
            )}
            {/* Warning when email differs from original */}
            {emailBlurred && originalEmail && billingDetails.email !== originalEmail && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1, color: '#b45309' }}>
                <WarningAmberIcon sx={{ fontSize: 16 }} />
                <Typography variant="caption" sx={{ color: '#b45309' }}>
                  Updating the receipt email will update existing user login details
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Stripe Payment Element */}
        <PaymentElement
          id="payment-element"
          options={paymentElementOptions}
          onChange={handlePaymentElementChange}
        />
      </Box>
    );
  }
);

CardPaymentInnerForm.displayName = 'CardPaymentInnerForm';

/**
 * Card Payment Form
 * 
 * A form for collecting card payment details using Stripe's Payment Element.
 * Requires a clientSecret from a PaymentIntent to render.
 * 
 * The parent component is responsible for:
 * - Creating the PaymentIntent and providing the clientSecret
 * - Updating the PaymentIntent when amount changes
 * - Saving the payment method to the database after confirmation
 */
const PaymentHubCardForm = forwardRef<CardPaymentRef, PaymentHubCardFormProps>(
  (
    {
      isActive,
      clientSecret,
      isLoading = false,
      billingDetails,
      onBillingDetailsChange,
      showNameFields = false,
      originalEmail,
    },
    ref
  ) => {
    const [isReady, setIsReady] = useState(false);
    const innerFormRef = useMemo(() => ({ current: null as CardPaymentRef | null }), []);

    const stripe = useMemo(() => getStripePromise(), []);

    useImperativeHandle(ref, () => ({
      createPaymentMethod: async (): Promise<CreateCardPaymentMethodResult> => {
        if (innerFormRef.current) {
          return innerFormRef.current.createPaymentMethod();
        }
        return {
          success: false,
          message: 'Payment form not ready.',
        };
      },
      confirmPayment: async (paymentMethodId: string): Promise<CardPaymentResult> => {
        if (innerFormRef.current) {
          return innerFormRef.current.confirmPayment(paymentMethodId);
        }
        return {
          success: false,
          message: 'Payment form not ready.',
        };
      },
      getBillingDetails: () => billingDetails,
      isReady,
    }));

    if (!isActive) {
      return null;
    }

    // Loading state
    if (isLoading && !clientSecret) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
          <CircularProgress size={32} />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
            Loading payment options...
          </Typography>
        </Box>
      );
    }

    // No client secret yet
    if (!clientSecret) {
      return (
        <Box py={2}>
          <Typography variant="body2" color="text.secondary">
            Loading payment options...
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ position: 'relative' }}>
        {/* Loading overlay while updating */}
        {isLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 10,
              borderRadius: 1,
            }}
          >
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              Updating...
            </Typography>
          </Box>
        )}
        <Elements 
          stripe={stripe} 
          options={{ 
            clientSecret, 
            // Required to use createPaymentMethod() separately from confirmPayment()
            paymentMethodCreation: 'manual',
          } as any}
        >
          <CardPaymentInnerForm
            ref={(r) => { innerFormRef.current = r; }}
            billingDetails={billingDetails}
            onBillingDetailsChange={onBillingDetailsChange}
            showNameFields={showNameFields}
            originalEmail={originalEmail}
            onReadyChange={setIsReady}
          />
        </Elements>
      </Box>
    );
  }
);

PaymentHubCardForm.displayName = 'PaymentHubCardForm';

export default PaymentHubCardForm;
